/**
 * Notification feature — schemas + display constants.
 *
 * Notification types:  success | info | warning | error
 * Notification categories: event | registration | user | system
 */

// ── Type display config (theme-token driven) ──────────────────────────────────

export const NOTIFICATION_TYPE_CONFIG = {
  success: {
    label:  'Success',
    color:  'var(--ac-success-fg)',
    bg:     'var(--ac-success-bg)',
    border: 'var(--ac-success)',
  },
  info: {
    label:  'Info',
    color:  'var(--ac-info-fg)',
    bg:     'var(--ac-info-bg)',
    border: 'var(--ac-info)',
  },
  warning: {
    label:  'Warning',
    color:  'var(--ac-warning-fg)',
    bg:     'var(--ac-warning-bg)',
    border: 'var(--ac-warning)',
  },
  error: {
    label:  'Error',
    color:  'var(--ac-danger-fg)',
    bg:     'var(--ac-danger-bg)',
    border: 'var(--ac-danger)',
  },
}

export const NOTIFICATION_CATEGORY_CONFIG = {
  event:        { label: 'Event',        color: 'var(--ac-secondary)' },
  registration: { label: 'Registration', color: 'var(--ac-success)'   },
  user:         { label: 'User',         color: 'var(--ac-info)'      },
  system:       { label: 'System',       color: 'var(--ac-warning)'   },
}

export const NOTIFICATION_FILTER_TABS = [
  { value: 'all',    label: 'All'    },
  { value: 'unread', label: 'Unread' },
  { value: 'read',   label: 'Read'   },
]

export const NOTIFICATION_TYPE_TABS = [
  { value: 'all',     label: 'All Types' },
  { value: 'success', label: 'Success'   },
  { value: 'info',    label: 'Info'      },
  { value: 'warning', label: 'Warning'   },
  { value: 'error',   label: 'Error'     },
]

// ── Template categories ───────────────────────────────────────────────────────

export const TEMPLATE_CATEGORIES = [
  { value: 'all',          label: 'All Templates'  },
  { value: 'registration', label: 'Registration'   },
  { value: 'event',        label: 'Event'          },
  { value: 'account',      label: 'Account'        },
  { value: 'onboarding',   label: 'Onboarding'     },
  { value: 'custom',       label: 'Custom'         },
]

// ── Template types (purpose-based, admin-friendly) ────────────────────────────

export const TEMPLATE_TYPES = [
  { value: 'welcome',        label: 'Welcome User',             category: 'onboarding'   },
  { value: 'assignment',     label: 'User Assigned to Event',   category: 'registration' },
  { value: 'reminder',       label: 'Event Reminder',           category: 'event'        },
  { value: 'event_updated',  label: 'Event Updated',            category: 'event'        },
  { value: 'cancelled',      label: 'Event Cancelled',          category: 'event'        },
  { value: 'password_reset', label: 'Password Reset',           category: 'account'      },
  { value: 'activated',      label: 'Account Activated',        category: 'account'      },
  { value: 'custom',         label: 'Custom',                   category: 'custom'       },
]

// ── Supported variables (admin-facing: click to insert) ───────────────────────

export const SUPPORTED_VARIABLES = [
  { key: 'userName',         label: 'User Name',         description: 'Recipient full name'        },
  { key: 'eventName',        label: 'Event Name',         description: 'Name of the event'          },
  { key: 'eventDate',        label: 'Event Date',         description: 'Formatted event date'       },
  { key: 'eventLocation',    label: 'Event Location',     description: 'City / venue of the event'  },
  { key: 'organizationName', label: 'Organization',       description: 'Admin organisation name'    },
  { key: 'assignedBy',       label: 'Assigned By',        description: 'Admin who created the assignment' },
  { key: 'loginUrl',         label: 'Login URL',          description: 'Hash-routed login page link' },
  { key: 'myEventsUrl',      label: 'My Events URL',      description: 'Portal My Events page link' },
  { key: 'portalDashboardUrl', label: 'Portal Dashboard', description: 'Portal dashboard page link' },
  { key: 'adminDashboardUrl',  label: 'Admin Dashboard',  description: 'Admin dashboard page link' },
]

// ── Preview substitution values (shown in the live preview panel) ─────────────

export const PREVIEW_EXAMPLES = {
  userName:           'John Doe',
  eventName:          'Agri Expo 2026',
  eventDate:          '15 March 2026',
  eventLocation:      'Pune',
  organizationName:   'AgroCast Analytics',
  assignedBy:         'Admin',
  loginUrl:           'https://aftersummit.com/#/auth/login',
  myEventsUrl:        'https://aftersummit.com/#/portal/my-events',
  portalDashboardUrl: 'https://aftersummit.com/#/portal/dashboard',
  adminDashboardUrl:  'https://aftersummit.com/#/admin/dashboard',
  resetLink:          'https://aftersummit.com/#/auth/reset-password?token=example',
  expiryTime:         '24 hours',
  supportEmail:       'support@agrocastanalytics.com',
  inviterName:        'Priya Sharma',
  inviteLink:         'https://aftersummit.com/#/auth/register?invite=example',
  assignmentId:       'ASN-2026-0042',
  assignmentStatus:   'Active',
}

// ── Legacy alias so existing imports don't break ──────────────────────────────
export const VARIABLE_EXAMPLES = PREVIEW_EXAMPLES
